import { useEffect, useState } from 'react';
import {
  Card,
  Stack,
  Button,
  Box,
  CircularProgress,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  IconButton,
  MenuItem,
  Popover,
  Chip,
} from '@mui/material';
import Scrollbar from 'src/components/scrollbar';
import { useNavigate } from 'react-router-dom';
import Iconify from 'src/components/iconify';
import ContestScoreDialog from 'src/components/contest/ContestScoreDialog';
import DeleteDialog from 'src/components/pages/DeleteDialog';

import { useGetContestQuery } from 'src/api/contests/getContest';
import { ContestType, WeekType } from 'src/types/domain.types';
import { useAppSearchParams } from 'src/hooks/useAppSearchParams';
import { useScoreContestMutation } from 'src/api/contests/scoreContest';
import { useDeleteRoundMutation } from 'src/api/contest-weeks/deleteRound';
import { WeekStatus } from 'src/enums/contest.enum';
import { ContestRoundDialog } from './ContestRoundDialog';
import { useTimezoneStore } from 'src/store/timezoneStore';
import { dateUtils } from 'src/utils/date';

interface RoundsSectionProps {
  contest: ContestType;
}

type TableHeader = {
  id: string;
  label: string;
  align?: 'left' | 'center' | 'right';
};

export const RoundsSection = ({ contest }: RoundsSectionProps) => {
  const navigate = useNavigate();
  const { contestId } = useAppSearchParams();
  const { timezone } = useTimezoneStore();

  const [round, setRound] = useState<WeekType | null>(null);
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
  const [openDelete, setOpenDelete] = useState<any>(null);
  const [deleteRound, setDeleteRound] = useState<any>(null);
  const [openRound, setOpenRound] = useState<HTMLElement | null>(null);
  const [openRoundId, setOpenRoundId] = useState<string | null>(null);

  const { isLoading: isGetContestLoading } = useGetContestQuery(contestId);
  const { mutateAsync: scoreContest, isPending: isScoreContestLoading } =
    useScoreContestMutation();
  const { mutateAsync: deleteRoundMutation, isPending: isDeleteRoundLoading } =
    useDeleteRoundMutation(contestId);

  useEffect(() => {
    if (!contestId) {
      return navigate('/dashboard/contests');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateRound = () => {
    setRound({
      start_utc: new Date().toISOString(),
      end_utc: new Date().toISOString(),
      status: WeekStatus.Draft,
      name_en: '',
      name_fr: '',
      name_es: '',
      name_lt: '',
      name_ru: '',
      name_de: '',
      name_sp: '',
      name_ca: '',
      is_back_in_enabled: false,
      is_buy_in_enabled: false,
      entry_fee: 0,
      matches: [],
    } as unknown as WeekType);
  };

  const handleOpenMenu = (event: any) => {
    setOpenRound(event.currentTarget);
    setOpenRoundId(event.currentTarget.id);
  };

  const handleCloseMenu = () => {
    setOpenRound(null);
    setOpenRoundId(null);
  };

  const handleScore = () => {
    scoreContest({ contestId, contestWeekId: Number(openRoundId) });
    handleCloseMenu();
  };

  const onEditRoundClick = () => {
    const round = contest.weeks.find(
      (week: WeekType) => week.contest_week_id === Number(openRoundId)
    ) as WeekType;
    setRound(round);
    handleCloseMenu();
  };

  const onDeleteRoundClick = () => {
    const round = contest.weeks.find(
      (week: WeekType) => week.contest_week_id === Number(openRoundId)
    ) as WeekType;
    setDeleteRound(round);
    setOpenDelete(true);
    handleCloseMenu();
  };

  const handleCloseDialog = () => {
    setRound(null);
    setOpenDelete(false);
  };

  const handleDeleteRound = () => {
    deleteRoundMutation(deleteRound.contest_week_id);
  };

  const tableHeader: TableHeader[] = [
    { id: 'name', label: 'Name' },
    { id: 'status', label: 'Status' },
    { id: 'entry_fee', label: 'Entry Fee' },
    { id: 'start_utc', label: `Start (${timezone})` },
    { id: 'end_utc', label: `End (${timezone})` },
    { id: 'actions', label: '', align: 'right' },
  ];

  return (
    <>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        mt={5}
      >
        <Typography variant='h4' gutterBottom>
          Rounds
        </Typography>
        <Box>
          <Button
            variant='contained'
            startIcon={<Iconify icon='eva:plus-outline' />}
            onClick={() => handleCreateRound()}
          >
            Create Round
          </Button>
        </Box>
      </Stack>
      <Card>
        {isGetContestLoading ||
        isScoreContestLoading ||
        isDeleteRoundLoading ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center' }}
            style={{ padding: 20 }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Scrollbar>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                <TableHead>
                  <TableRow>
                    {tableHeader.map(({ id, label, align }) => (
                      <TableCell key={id} align={align}>
                        {label}
                      </TableCell>
                    ))}
                    <TableCell align='right'></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contest?.weeks.map((week: WeekType, index: any) => (
                    <TableRow
                      key={index}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                      }}
                    >
                      <TableCell component='th' scope='row'>
                        {week.name_en}
                      </TableCell>

                      <TableCell component='th' scope='row'>
                        {week.status === WeekStatus.Draft ? (
                          <Chip label='Draft' color='default' />
                        ) : week.status === WeekStatus.Active ? (
                          <Chip label='Active' color='success' />
                        ) : (
                          <Chip label='Upcoming' color='primary' />
                        )}
                      </TableCell>
                      <TableCell component='th' scope='row'>
                        {week.entry_fee}
                      </TableCell>
                      <TableCell component='th' scope='row'>
                        {dateUtils.formatForDisplay(week.start_utc, timezone)}
                      </TableCell>
                      <TableCell component='th' scope='row'>
                        {dateUtils.formatForDisplay(week.end_utc, timezone)}
                      </TableCell>

                      <TableCell align='right'>
                        <IconButton
                          size='large'
                          color='inherit'
                          onClick={handleOpenMenu}
                          id={week.contest_week_id.toString()}
                        >
                          <Iconify icon={'eva:more-vertical-fill'} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
        )}
      </Card>

      <Popover
        open={Boolean(openRound)}
        anchorEl={openRound}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleScore}>
          <Iconify icon={'eva:play-circle-outline'} sx={{ mr: 2 }} />
          Run Scoring
        </MenuItem>
        <MenuItem onClick={onEditRoundClick}>
          <Iconify icon={'eva:edit-outline'} sx={{ mr: 2 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={onDeleteRoundClick}>
          <Iconify icon={'eva:trash-outline'} sx={{ mr: 2 }} />
          Delete
        </MenuItem>
      </Popover>

      {round && (
        <ContestRoundDialog
          round={round}
          onClose={() => setRound(null)}
          contestId={contestId}
        />
      )}

      {scoreDialogOpen && (
        <ContestScoreDialog
          weeks={contest.weeks}
          contestId={contestId}
          onClose={() => setScoreDialogOpen(false)}
        />
      )}

      {openDelete && (
        <DeleteDialog
          openConfirm={openDelete}
          type={`round "${deleteRound?.name_en}"`}
          id={deleteRound?.contest_week_id}
          deleteHandler={handleDeleteRound}
          setOpenConfirm={handleCloseDialog}
        />
      )}
    </>
  );
};
